import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import EventStatusSelector from './EventStatusSelector';
import EventBaseEditModal from './EventBaseEditModal';
import it from 'date-fns/locale/it';
registerLocale('it', it);
setDefaultLocale('it');

export default function EventForm({ onCreated, lightMode, initialData }) {
  const [form, setForm] = useState({
    name: '',
    location: '',
    date: '',
    status: 0,
    CityId: '',
    startTime: '',
    endTime: '',
    description: '',
    priceIncludes: '',
    priceExcludes: '',
    showHowItWorks: false,
    howItWorksTemplateKey: '',
    coverImage: '',
    soldOut: false
  });
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [availableTicketTypes, setAvailableTicketTypes] = useState([]);
  const [eventTickets, setEventTickets] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCoverLibrary, setShowCoverLibrary] = useState(false);
  const [coverLibrary, setCoverLibrary] = useState([]);
  const [showBaseEdit, setShowBaseEdit] = useState(false);

  useEffect(() => {
    fetchCities();
    fetchTicketTypes();
    fetchTags();
    fetchTemplates();
    fetchCoverLibrary();
  }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get('/speeddate/api/cities');
      setCities(res.data);
    } catch (err) {
      console.error("Error fetching cities", err);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const res = await axios.get('/speeddate/api/tickets');
      setAvailableTicketTypes(res.data);
    } catch (err) {
      console.error("Error fetching ticket types", err);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get('/speeddate/api/tags');
      setAllTags(res.data);
    } catch (err) {
      console.error('Error fetching tags', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/speeddate/api/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates', err);
    }
  };

  const fetchCoverLibrary = async () => {
    try {
      const res = await axios.get('/speeddate/api/cover-images');
      setCoverLibrary(res.data);
    } catch (err) {
      console.error('Error fetching cover library', err);
    }
  };

  useEffect(() => {
    if (initialData) {
      const formattedDate = initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : '';
      setForm({
        name: initialData.name || '',
        location: initialData.location || '',
        date: formattedDate,
        status: initialData.status !== undefined ? initialData.status : 0,
        CityId: initialData.CityId || '',
        startTime: initialData.startTime ? initialData.startTime.slice(0, 5) : '',
        endTime: initialData.endTime ? initialData.endTime.slice(0, 5) : '',
        description: initialData.description || '',
        priceIncludes: initialData.priceIncludes || '',
        priceExcludes: initialData.priceExcludes || '',
        howItWorksTemplateKey: initialData.howItWorksTemplateKey || '',
        coverImage: initialData.coverImage || '',
        soldOut: initialData.soldOut || false,
        ultimiPostiUomo: initialData.ultimiPostiUomo || false,
        ultimiPostiDonna: initialData.ultimiPostiDonna || false
      });
      // Set existing ticket types for this event
      if (initialData.EventTicketTypes) {
        setEventTickets(initialData.EventTicketTypes.map(et => ({
          TicketId: et.TicketId,
          price: et.price,
          fullprice: (et.fullprice === undefined || et.fullprice === null) ? null : et.fullprice,
          promo: et.fullprice !== undefined && et.fullprice !== null && et.fullprice !== ''
        })));
      } else {
        setEventTickets([]);
      }
      // Set existing tags
      if (initialData.tags) {
        setSelectedTagIds(initialData.tags.map(t => t.id));
      } else {
        setSelectedTagIds([]);
      }
    } else {
      setForm({
        name: '',
        location: '',
        date: '',
        status: 0,
        CityId: '',
        startTime: '',
        endTime: '',
        description: '',
        priceIncludes: '',
        priceExcludes: '',
        howItWorksTemplateKey: '',
        coverImage: '',
        soldOut: false,
        ultimiPostiUomo: false,
        ultimiPostiDonna: false
      });
      setEventTickets([]);
      setSelectedTagIds([]);
    }
  }, [initialData]);

  const handleToggleTicket = (ticketId) => {
    setEventTickets(prev => {
      const exists = prev.find(et => et.TicketId === ticketId);
      if (exists) {
        return prev.filter(et => et.TicketId !== ticketId);
      } else {
        return [...prev, { TicketId: ticketId, price: 0, fullprice: null, promo: false }];
      }
    });
  };

  const handlePriceChange = (ticketId, price) => {
    setEventTickets(prev => prev.map(et =>
      et.TicketId === ticketId ? { ...et, price: parseFloat(price) || 0 } : et
    ));
  };

  // SOLA-71: Promo — la spunta attiva la possibilità di impostare il "Prezzo
  // Pieno" (fullprice). Togliendo la spunta il biglietto torna a prezzo pieno.
  const isPromo = (et) => !!et.promo;

  const handlePromoToggle = (ticketId, checked) => {
    setEventTickets(prev => prev.map(et =>
      et.TicketId === ticketId
        ? {
            ...et,
            promo: checked,
            fullprice: checked
              ? (et.fullprice !== null && et.fullprice !== undefined ? et.fullprice : '')
              : null
          }
        : et
    ));
  };

  const handleFullPriceChange = (ticketId, fullprice) => {
    setEventTickets(prev => prev.map(et =>
      et.TicketId === ticketId
        ? { ...et, promo: true, fullprice: fullprice === '' ? '' : parseFloat(fullprice) || 0 }
        : et
    ));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, coverImage: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSelectFromLibrary = async (img) => {
    try {
      const resp = await fetch(img.url);
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({ ...prev, coverImage: reader.result }));
        setShowCoverLibrary(false);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error loading image from library', err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...form, eventTickets };
      // SOLA-70: la lista eventi non restituisce più coverImage (base64, ~1.4MB/evento)
      // quindi in modifica avviata dalla lista il campo cover è vuoto. Non inviarlo
      // alla PUT: altrimenti la copertina esistente verrebbe azzerata.
      if (initialData && initialData.id && !form.coverImage) {
        delete payload.coverImage;
      }
      let eventId;
      if (initialData && initialData.id) {
        await axios.put(`/speeddate/api/events/${initialData.id}`, payload);
        eventId = initialData.id;
        window.Swal.fire({
          title: 'Evento Aggiornato!',
          text: 'Le modifiche sono state salvate.',
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        });
        // Save tags for updates (separate call)
        if (selectedTagIds.length > 0) {
          await axios.post(`/speeddate/api/events/${eventId}/tags`, { tagIds: selectedTagIds }).catch(() => { });
        } else {
          await axios.post(`/speeddate/api/events/${eventId}/tags`, { tagIds: [] }).catch(() => { });
        }
      } else {
        // Include tagIds in creation payload so notification sees them
        const res = await axios.post('/speeddate/api/events', { ...payload, tagIds: selectedTagIds });
        eventId = res.data.id;
        window.Swal.fire({
          title: 'Evento Creato!',
          text: 'L\'evento è stato aggiunto con successo.',
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        });
        setForm({
          name: '',
          location: '',
          date: '',
          status: 0,
          CityId: '',
          startTime: '',
          endTime: '',
          description: '',
          priceIncludes: '',
          priceExcludes: '',
          howItWorksTemplateKey: '',
          coverImage: ''
        });
        setEventTickets([]);
        setSelectedTagIds([]);
      }
      if (onCreated) onCreated();
    } catch (err) {
      window.Swal.fire('Errore', "Si è verificato un problema.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `form-control ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none placeholder-white-50' : ''}`;
  const selectClass = `form-select ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none' : ''}`;
  const labelClass = `form-label small fw-bold ${lightMode ? 'text-white text-opacity-75' : 'text-muted'}`;

  const isLocked = initialData && initialData.status > 3;
  const isFinalized = initialData && initialData.status > 5;

  // SOLA-49: se l'evento ha partecipanti registrati, i campi base
  // (data, ora, città, venue) non sono modificabili dalla modifica normale:
  // serve il pulsante rosso MODIFICA dedicato (Endpoint /base-fields).
  const registeredParticipants = (initialData && Array.isArray(initialData.participants))
    ? initialData.participants.filter(p => p.status !== 'cancelled')
    : [];
  const hasParticipants = registeredParticipants.length > 0;
  const baseLocked = isLocked || hasParticipants;

  return (
    <form onSubmit={submit}>
      <style>{`
        [data-s49-tooltip]{position:relative;display:inline-block}
        [data-s49-tooltip]:hover::before,[data-s49-tooltip]:focus::before{
          content:'';position:absolute;left:50%;top:calc(100% + 4px);transform:translateX(-50%);border:0 solid transparent;border-width:0 5px 6px 5px;border-bottom-color:#1f2937;z-index:3100;pointer-events:none;
        }
        [data-s49-tooltip]:hover::after,[data-s49-tooltip]:focus::after{
          content:attr(data-s49-tooltip);position:absolute;left:50%;top:calc(100% + 10px);transform:translateX(-50%);background:#1f2937;color:#fff;padding:7px 12px;border-radius:8px;font-size:11px;line-height:1.35;white-space:normal;max-width:300px;width:max-content;z-index:3101;pointer-events:none;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,.35);text-align:center;
        }
      `}</style>
      {isLocked && (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.8rem' }}>
          <i className="fas fa-lock me-2"></i>
          Evento bloccato perché in corso o terminato.
        </div>
      )}
      {isFinalized && (
        <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.8rem' }}>
          <i className="fas fa-ban me-2"></i>
          Evento con risultati pubblicati o archiviato (stato <strong>&gt; 5</strong>): <strong>non modificabile</strong>.
        </div>
      )}
      <div className="mb-3">
        <label className={labelClass}>Titolo Evento</label>
        <input
          type="text"
          className={inputClass}
          placeholder="es. Gran Galà SpeedDate"
          required
          disabled={isLocked}
          style={lightMode ? { colorScheme: 'dark' } : {}}
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
        />
      </div>
      {/* SOLA-49 — Dati base evento: bloccati (e modificabili solo da endpoint dedicato) se l'evento ha partecipanti */}
      <div className="card border-0 bg-light mb-4 shadow-sm">
        <div className="card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0 small fw-bold"><i className="fas fa-map-marker-alt me-1"></i> Dati Base Evento (data, ora, città, venue)</h6>
          {hasParticipants && !isFinalized && (
            <span className="d-inline-flex align-items-center">
              <button
                type="button"
                className="btn btn-danger btn-sm rounded-pill fw-bold shadow-sm"
                data-s49-tooltip="Modifica dati base dell'evento con notifica email ai partecipanti"
                aria-label="Modifica dati base dell'evento con notifica email ai partecipanti"
                onClick={() => setShowBaseEdit(true)}
              >
                <i className="fas fa-pen me-1"></i> MODIFICA
              </button>
            </span>
          )}
        </div>
        <div className="card-body p-3">
          {hasParticipants && !isFinalized && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.8rem' }}>
              <i className="fas fa-lock me-2"></i>
              Campi bloccati: {registeredParticipants.length} partecipante{registeredParticipants.length === 1 ? '' : 'i'} registrato{registeredParticipants.length === 1 ? '' : 'i'}.
              Per cambiare data, ora, città o venue usa il pulsante rosso <strong>MODIFICA</strong>: verrà inviata un'email di notifica a tutti i partecipanti.
            </div>
          )}
      <div className="mb-3">
        <label className={labelClass}>Locale / Indirizzo</label>
        <input
          type="text"
          className={inputClass}
          placeholder="es. Virtual Bar, Roma"
          required
          disabled={baseLocked}
          value={form.location}
          onChange={e => setForm({ ...form, location: e.target.value })}
        />
      </div>
      <div className="mb-3">
        <label className={labelClass}>Città</label>
        <select
          className={selectClass}
          required
          disabled={baseLocked}
          value={form.CityId}
          onChange={e => setForm({ ...form, CityId: e.target.value })}
          style={lightMode ? { colorScheme: 'dark' } : {}}
        >
          <option value="" disabled>Seleziona Città...</option>
          {cities.map(city => (
            <option key={city.id} value={city.id} style={{ color: '#000' }}>
              {city.name} {city.province ? `(${city.province})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className={labelClass}>Data</label>
          <DatePicker
            selected={form.date ? new Date(form.date + 'T00:00:00') : null}
            onChange={date => {
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              setForm({ ...form, date: `${y}-${m}-${d}` });
            }}
            dateFormat="dd/MM/yyyy"
            locale="it"
            className={`form-control ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none' : ''}`}
            disabled={baseLocked}
            required
            placeholderText="Seleziona data"
            isClearable={!baseLocked}
            wrapperClassName="w-100"
          />
        </div>
        <div className="col-md-3">
          <label className={labelClass}>Inizio</label>
          <DatePicker
            selected={form.startTime ? new Date(`2000-01-01T${form.startTime}:00`) : null}
            onChange={date => setForm({ ...form, startTime: date ? date.toTimeString().slice(0, 5) : '' })}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Ora"
            dateFormat="HH:mm"
            locale="it"
            className={`form-control ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none' : ''}`}
            disabled={baseLocked}
            placeholderText="HH:MM"
            wrapperClassName="w-100"
          />
        </div>
        <div className="col-md-3">
          <label className={labelClass}>Fine</label>
          <DatePicker
            selected={form.endTime ? new Date(`2000-01-01T${form.endTime}:00`) : null}
            onChange={date => setForm({ ...form, endTime: date ? date.toTimeString().slice(0, 5) : '' })}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Ora"
            dateFormat="HH:mm"
            locale="it"
            className={`form-control ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none' : ''}`}
            disabled={baseLocked}
            placeholderText="HH:MM"
            wrapperClassName="w-100"
          />
        </div>
      </div>
        </div>
      </div>

      <div className="card border-0 bg-light mb-4 shadow-sm overflow-hidden">
        <div className="card-header bg-dark text-white py-2">
          <h6 className="mb-0 small fw-bold">Gestione Biglietti Evento</h6>
        </div>
        <div className="card-body p-3">
          <div className="row g-3">
            {availableTicketTypes.map(tt => {
              const selected = eventTickets.find(et => et.TicketId === tt.id);
              const promo = selected && isPromo(selected);
              return (
                <div key={tt.id} className="col-12 col-md-6">
                  <div className={`p-2 rounded border ${selected ? 'border-primary bg-white shadow-sm' : 'border-secondary opacity-75'}`}>
                    <div className="form-check d-flex align-items-center mb-1">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id={`tt-${tt.id}`}
                        checked={!!selected}
                        onChange={() => handleToggleTicket(tt.id)}
                        disabled={isLocked}
                      />
                      <label className="form-check-label fw-bold small flex-grow-1" htmlFor={`tt-${tt.id}`}>
                        {tt.name}
                      </label>
                    </div>
                    {selected && (
                      <div className="input-group input-group-sm mt-1">
                        <span className="input-group-text bg-light border-0 small">€</span>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control border-0 bg-light"
                          placeholder="Prezzo"
                          value={selected.price}
                          onChange={(e) => handlePriceChange(tt.id, e.target.value)}
                          disabled={isLocked}
                        />
                      </div>
                    )}
                    {selected && (
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <div className="form-check form-check-inline mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`promo-${tt.id}`}
                            checked={promo}
                            onChange={(e) => handlePromoToggle(tt.id, e.target.checked)}
                            disabled={isLocked}
                          />
                          <label className="form-check-label small fw-bold text-warning" htmlFor={`promo-${tt.id}`}>
                            <i className="fas fa-tag me-1"></i>Promo
                          </label>
                        </div>
                        {promo && (
                          <div className="input-group input-group-sm flex-grow-1">
                            <span className="input-group-text bg-light border-0 small">€</span>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control border-0 bg-light"
                              placeholder="Prezzo Pieno"
                              value={selected.fullprice ?? ''}
                              onChange={(e) => handleFullPriceChange(tt.id, e.target.value)}
                              disabled={isLocked}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {availableTicketTypes.length === 0 && (
            <div className="text-center text-muted small py-3">
              Nessun tipo di biglietto disponibile nell'anagrafica.
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Tag / Categorie</label>
        <div className="card border-0 bg-light overflow-hidden">
          <div className="card-body p-3">
            {allTags.filter(t => t.type === 'etn_category').length > 0 && (
              <>
                <div className="small fw-bold mb-2 text-muted">
                  <i className="fas fa-tag me-1" style={{ color: '#8b5cf6' }}></i> Categorie
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {allTags.filter(t => t.type === 'etn_category').map(t => (
                    <span key={t.id}
                      className={`badge rounded-pill px-3 py-2 cursor-pointer ${selectedTagIds.includes(t.id) ? 'text-white' : 'text-dark border'}`}
                      style={{
                        background: selectedTagIds.includes(t.id) ? '#8b5cf6' : '#f3f4f6',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        border: selectedTagIds.includes(t.id) ? 'none' : '1px solid #d1d5db'
                      }}
                      onClick={() => setSelectedTagIds(prev => prev.includes(t.id) ? prev.filter(id => id !== t.id) : [...prev, t.id])}
                    >{t.name}</span>
                  ))}
                </div>
              </>
            )}
            {allTags.filter(t => t.type === 'etn_tags').length > 0 && (
              <>
                <div className="small fw-bold mb-2 text-muted">
                  <i className="fas fa-tags me-1" style={{ color: '#f59e0b' }}></i> Tag età / altro
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {allTags.filter(t => t.type === 'etn_tags').map(t => (
                    <span key={t.id}
                      className={`badge rounded-pill px-3 py-2 cursor-pointer ${selectedTagIds.includes(t.id) ? 'text-white' : 'text-dark border'}`}
                      style={{
                        background: selectedTagIds.includes(t.id) ? '#f59e0b' : '#f3f4f6',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        border: selectedTagIds.includes(t.id) ? 'none' : '1px solid #d1d5db'
                      }}
                      onClick={() => setSelectedTagIds(prev => prev.includes(t.id) ? prev.filter(id => id !== t.id) : [...prev, t.id])}
                    >{t.name}</span>
                  ))}
                </div>
              </>
            )}
            {allTags.length === 0 && (
              <div className="text-muted small py-2">
                <i className="fas fa-spinner fa-spin me-2"></i> Caricamento tag...
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Descrizione HTML (Copia/Incolla)</label>
        <textarea
          className={inputClass}
          rows="4"
          placeholder="Incolla qui il codice HTML della descrizione..."
          disabled={isLocked}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        ></textarea>
      </div>

      <div className="mb-4">
        <label className={labelClass}>La quota cosa comprende</label>
        <textarea
          className={inputClass}
          rows="3"
          placeholder="es.Ticket di partecipazione all'evento"
          disabled={isLocked}
          value={form.priceIncludes}
          onChange={e => setForm({ ...form, priceIncludes: e.target.value })}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        ></textarea>
      </div>

      <div className="mb-4">
        <label className={labelClass}>La quota cosa non comprende</label>
        <textarea
          className={inputClass}
          rows="3"
          placeholder="es. Apericena 15€"
          disabled={isLocked}
          value={form.priceExcludes}
          onChange={e => setForm({ ...form, priceExcludes: e.target.value })}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        ></textarea>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Sezione "Come funziona"</label>
        <select
          className="form-select"
          value={form.howItWorksTemplateKey}
          disabled={isLocked}
          onChange={e => setForm({ ...form, howItWorksTemplateKey: e.target.value })}
        >
          <option value="">Nessuno (non mostrare)</option>
          {templates.map(t => (
            <option key={t.key} value={t.key}>{t.name}</option>
          ))}
        </select>
        <div className="text-muted small mt-1">
          Il contenuto è gestito globalmente nella pagina
          <a href="/admin?view=templates" target="_blank" className="ms-1 text-link">Templates</a>.
        </div>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Immagine di Copertina</label>
        <div className="d-flex align-items-start gap-3 flex-wrap">
          {form.coverImage && (
            <div className="rounded overflow-hidden shadow-sm" style={{ width: '80px', height: '80px', flexShrink: 0 }}>
              <img src={form.coverImage} alt="Cover Preview" className="w-100 h-100 object-fit-cover" />
            </div>
          )}
          <div className="flex-grow-1" style={{ minWidth: '200px' }}>
            <div className="d-flex gap-2 mb-2">
              <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setShowCoverLibrary(true)} disabled={isLocked}>
                <i className="fas fa-images me-1"></i> Seleziona da libreria
              </button>
              <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0" style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                <i className="fas fa-upload me-1"></i> Carica nuovo
                <input type="file" accept="image/*" className="d-none" disabled={isLocked} onChange={handleFileChange} />
              </label>
            </div>
            {form.coverImage && (
              <button type="button" className="btn btn-sm btn-link text-danger p-0 small" onClick={() => setForm({ ...form, coverImage: '' })} disabled={isLocked}>
                <i className="fas fa-times me-1"></i>Rimuovi immagine
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cover Library Modal */}
      {showCoverLibrary && (
        <div className="modal show" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowCoverLibrary(false)}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', maxHeight: '80vh' }}>
              <div className="modal-header border-0 pt-4 px-4">
                <h5 className="modal-title fw-bold"><i className="fas fa-images text-primary me-2"></i>Libreria Copertine</h5>
                <button type="button" className="btn-close" onClick={() => setShowCoverLibrary(false)}></button>
              </div>
              <div className="modal-body p-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                {coverLibrary.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-image fa-3x mb-3 opacity-25"></i>
                    <p className="mb-0">Nessuna immagine nella libreria.</p>
                    <p className="small">Carica immagini dalla sezione Copertine del menu admin.</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {coverLibrary.map(img => (
                      <div key={img.filename} className="col-6 col-md-4 col-lg-3">
                        <div
                          className={`card border-0 shadow-sm h-100 ${form.coverImage === img.url ? 'ring-2 ring-primary' : ''}`}
                          style={{ cursor: 'pointer', outline: form.coverImage === img.url ? '3px solid #6366f1' : 'none', transition: 'all 0.15s' }}
                          onClick={() => handleSelectFromLibrary(img)}
                        >
                          <div style={{ paddingTop: '66%', overflow: 'hidden' }}>
                            <img
                              src={img.url}
                              alt={img.filename}
                              className="w-100 h-100 position-absolute top-0 start-0"
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                          <div className="p-2 small text-muted text-truncate">
                            {img.filename}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={() => setShowCoverLibrary(false)}>
                  Annulla
                </button>
                <button type="button" className="btn btn-primary rounded-pill px-4 shadow-sm" onClick={() => setShowCoverLibrary(false)}>
                  Conferma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <EventStatusSelector
          event={form}
          onChange={(payload) => setForm({ ...form, ...payload })}
          disabled={isLocked}
        />
      </div>

      {showBaseEdit && initialData && (
        <EventBaseEditModal
          event={initialData}
          onClose={() => setShowBaseEdit(false)}
          onSaved={() => { setShowBaseEdit(false); if (onCreated) onCreated(); }}
        />
      )}

      <div className="d-grid pt-2">
        <button type="submit" className={`btn ${lightMode ? 'btn-white text-primary fw-bold' : 'btn-primary'} shadow-sm`} disabled={loading || isLocked}>
          {loading ? 'Attendere...' : (initialData ? 'Aggiorna Evento' : 'Crea Evento')}
        </button>
      </div>
    </form>
  );
}